import { useEffect, useMemo, useState } from "react";
import api from "../services/api";
import companyService from "../services/companyService";
import { isAdminRole } from "../utils/roles";

export type CompanyOption = { id: string; name: string };

type UseCompanySelectorResult = {
  isAdmin: boolean;
  loading: boolean;
  companies: CompanyOption[];
  selectedCompanyId: string | null;
  setSelectedCompanyId: (id: string | null) => void;
  currentCompanyId: string | null;
};

/**
 * Encapsulates role detection, company fetching, and selection state used by pages.
 * - Detects admin via /auth/me and isAdminRole helper
 * - For admins: fetches all companies and exposes a selectable list
 * - For non-admins: binds selection to the user's own company
 */
export function useCompanySelector(): UseCompanySelectorResult {
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [currentCompanyId, setCurrentCompanyId] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function bootstrap() {
      setLoading(true);
      try {
        // Load current user to determine role and company
        const meResp = await api.get("/auth/me");
        const me = meResp?.data;
        const role = me?.role || me?.userRole || me?.user?.role;
        const admin = isAdminRole(role);
        const myCompanyId: string | null = (me?.companyId || me?.company?._id || me?.company?.id || null) ? String(me?.companyId || me?.company?._id || me?.company?.id) : null;

        if (!mounted) return;
        setIsAdmin(admin);
        setCurrentCompanyId(myCompanyId);

        if (admin) {
          const all = await companyService.getAllCompanies();
          // Normalize to id/name for selector
          const opts: CompanyOption[] = (all || []).map((c: any) => ({ id: String(c.id), name: String(c.name || c.legalName || "") }));
          if (!mounted) return;
          setCompanies(opts);
          // Preserve previous selection if valid; otherwise default to first
          setSelectedCompanyId(prev => {
            if (prev && opts.some(o => o.id === prev)) return prev;
            return opts.length ? opts[0].id : null;
          });
        } else {
          // Bind to user's company
          setCompanies(myCompanyId ? [{ id: myCompanyId, name: me?.company?.name || me?.company?.legalName || "My Company" }] : []);
          setSelectedCompanyId(myCompanyId);
        }
      } catch (err) {
        if (!mounted) return;
        // Fallback: non-admin, no companies
        setIsAdmin(false);
        setCompanies([]);
        setSelectedCompanyId(null);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    bootstrap();
    return () => {
      mounted = false;
    };
  }, []);

  return useMemo(
    () => ({ isAdmin, loading, companies, selectedCompanyId, setSelectedCompanyId, currentCompanyId }),
    [isAdmin, loading, companies, selectedCompanyId, currentCompanyId]
  );
}

export default useCompanySelector;